/* oxlint-disable max-lines-per-function */
/* oxlint-disable no-nested-ternary */
import { getRequiredStreamPackIdsFromPeriods, getPlaylistPackIds, getFoundationsPlaylistPackId } from 'Cohorts';
import { formattedUserFacingMonthDayYearMedium } from 'DateHelpers';
import { useCurrentUser } from 'FrontRoyalAngular';
import { frontRoyalStoreApi } from 'FrontRoyalStore';
import { useProjectsForPlaylists } from 'LearnerProjects';
import { useMemo } from 'react';
import { type Stream } from 'Lessons';
import { isAssessmentPolicyWithCurriculumStarted } from 'RefundEntitlement/utils';
import { getCohort, getRefundEntitlement } from 'Users';
import { useContentProgress, isPassedOrWaived } from 'ContentProgress';
import { useSuspenseQuery } from 'ReduxHelpers';
import { type PlaylistsWithGrades } from './Grades.types';
import { formatScore, getProjectStatus } from './utils';

const shouldAddAsterisk = (
    courseLocalePackId: string,
    includeFoundationsInGrades: boolean,
    foundationsLessonStreamLocalePackIds: string[] | undefined,
    gradeSafeStreamLocalePackIds: string[],
) =>
    (!includeFoundationsInGrades && !!foundationsLessonStreamLocalePackIds?.find(id => id === courseLocalePackId)) ||
    gradeSafeStreamLocalePackIds.includes(courseLocalePackId);

const useGetContent = () => {
    const user = useCurrentUser();
    const cohort = getCohort(user);

    const { data: playlists = [] } = useSuspenseQuery(frontRoyalStoreApi, 'getPublishedPlaylists');
    const { data: courses = [] } = useSuspenseQuery(frontRoyalStoreApi, 'getPublishedStreams');

    // FIXME: move preferStrictSmartcaseScore from schedulable into program type config
    const includeFoundationsInGrades = useMemo(() => (cohort ? cohort.startDate < 1697443200 : false), [cohort]); // < Cycle 56

    const foundationsPlaylist = useMemo(() => {
        if (!cohort) return null;
        const foundationsPlaylistLocalePackId = getFoundationsPlaylistPackId(cohort);
        if (!foundationsPlaylistLocalePackId) return null;

        return playlists.find(p => p.localePackId === foundationsPlaylistLocalePackId) ?? null;
    }, [cohort, playlists]);

    const requiredStreamsPackIds = useMemo(() => (cohort ? getRequiredStreamPackIdsFromPeriods(cohort) : []), [cohort]);

    const playlistsAndSpecializations = useMemo(() => {
        if (!cohort || !playlists.length) return [];

        return getPlaylistPackIds(cohort)
            .filter(packId => {
                // Include all courses if foundationsInGrades is true
                if (includeFoundationsInGrades) return !!packId;

                // Exclude foundations playlist
                if (packId === foundationsPlaylist?.localePackId) return false;

                return !!packId;
            })
            .map(packId => {
                const playlist = playlists.find(p => p.localePack.id === packId)!;

                return playlist
                    ? {
                          ...playlist,
                      }
                    : null;
            })
            .filter(v => !!v);
    }, [cohort, foundationsPlaylist, includeFoundationsInGrades, playlists]);

    const assessmentCourses = useMemo(
        () => courses.map(c => ({ ...c, lessons: c.lessons.filter(l => l.assessment === true) })),
        [courses],
    );

    const nonPlaylistRequiredCourses = useMemo(() => {
        if (!cohort) return [];

        const playlistStreamPackIds = playlistsAndSpecializations.flatMap(p =>
            p.streamEntries.map(se => se.localePackId),
        );

        const nonPlaylistRequiredPackIds = requiredStreamsPackIds.filter(s => !playlistStreamPackIds.includes(s));

        return courses.filter(c => nonPlaylistRequiredPackIds.includes(c.localePack.id));
    }, [cohort, courses, playlistsAndSpecializations, requiredStreamsPackIds]);

    const projectsAndCapstones = useProjectsForPlaylists();
    const electives = useMemo(() => {
        if (!cohort) return [];

        return courses
            .flatMap(c => {
                if (c.exam) return null;
                if (requiredStreamsPackIds.includes(c.localePack.id)) return null;

                if (cohort.streamLocalePacksInfo[c.localePack.id]?.elective !== true) return null;
                const electiveSmartcases = c.lessons.filter(l => l.assessment === true);
                if (!electiveSmartcases.length) return null;

                return { course: c.title, smartcases: electiveSmartcases };
            })
            .filter(v => !!v);
    }, [cohort, courses, requiredStreamsPackIds]);

    return {
        playlistsAndSpecializations,
        assessmentCourses,
        nonPlaylistRequiredCourses,
        electives,
        foundationsPlaylist,
        includeFoundationsInGrades,
        ...projectsAndCapstones,
    };
};

export const useGradesData = () => {
    const user = useCurrentUser();
    const cohort = getCohort(user);

    const {
        playlistsAndSpecializations,
        projectsByPlaylist,
        capstones,
        assessmentCourses,
        nonPlaylistRequiredCourses,
        electives,
        foundationsPlaylist,
        includeFoundationsInGrades,
    } = useGetContent();

    const { lessonProgress, projectProgress, streamProgress: courseProgress } = useContentProgress();

    // Access groups whose content should be excluded from grade calculations
    // This is duplicated server-side in access_group.rb (and should be kept in sync)
    const GRADE_SAFE_ACCESS_GROUPS = useMemo(() => new Set(['OPEN COURSES 1', 'OPEN COURSES 2', 'EXTRA COURSES']), []);

    const gradeSafeStreamLocalePackIds = useMemo(
        () =>
            user?.groups
                ?.filter(group => GRADE_SAFE_ACCESS_GROUPS.has(group.name))
                ?.flatMap(group => group.streamLocalePackIds) || [],
        [user?.groups, GRADE_SAFE_ACCESS_GROUPS],
    );

    const [playlistsWithGrades, specializationsWithGrades] = useMemo(() => {
        if (!cohort || !user) return [[], []];

        return playlistsAndSpecializations.reduce(
            ([playlistGrades, specializationGrades], p) => {
                if (!p) return [playlistGrades, specializationGrades];

                const coursesForPlaylist = p.streamEntries
                    .map(se => assessmentCourses.find(c => c.localePack.id === se.localePackId) as Stream)
                    .filter(v => !!v);

                const objectWithGrades = {
                    title: p.title,
                    image:
                        p.localePackId === foundationsPlaylist?.localePackId
                            ? null
                            : coursesForPlaylist.filter(c => !c.exam).at(-1)?.image,
                    smartcases: coursesForPlaylist
                        .filter(c => !c.exam)
                        .flatMap(c =>
                            c.lessons.map(l => {
                                const progress = lessonProgress.find(lp => lp.localePackId === l.localePack.id);

                                // If we are not including foundations in the user's grades, we won't show the foundations playlist on the grades
                                // page, but we will show smartcases that are part of a foundations course that are required in other playlists
                                // we just want to add an asterisk to the title so we can reference it to ensure users these will not count
                                // towards their final grade
                                const addAsterisk = shouldAddAsterisk(
                                    c.localePack.id,
                                    includeFoundationsInGrades,
                                    cohort.foundationsLessonStreamLocalePackIds,
                                    gradeSafeStreamLocalePackIds,
                                );

                                return {
                                    title: l.title,
                                    course: addAsterisk ? (
                                        <div>
                                            {c.title}
                                            <p className="ms-[1px] inline-block pt-[1px] align-middle text-lg font-semibold leading-[0] text-beige-beyond-dark">
                                                *
                                            </p>
                                        </div>
                                    ) : (
                                        c.title
                                    ),
                                    score: formatScore(progress),
                                    completedAt: progress?.completedAt
                                        ? formattedUserFacingMonthDayYearMedium(
                                              new Date(progress.completedAt * 1000),
                                              false,
                                          )
                                        : null,
                                    complete: progress?.complete ?? false,
                                    started: !!progress,
                                    localePackId: l.localePack.id,
                                };
                            }),
                        ),
                    exams: coursesForPlaylist
                        .filter(c => !!c.exam)
                        .map(e => {
                            const examProgress = courseProgress.find(lp => lp.localePackId === e.localePack.id);
                            return {
                                title: e.title,
                                completedAt: examProgress?.completedAt
                                    ? formattedUserFacingMonthDayYearMedium(
                                          new Date(examProgress.completedAt * 1000),
                                          false,
                                      )
                                    : null,
                                score: formatScore(examProgress),
                                started: !!examProgress,
                            };
                        }),
                    projects:
                        projectsByPlaylist[p.localePackId]?.map(project => {
                            const progress = projectProgress.find(
                                pp => pp.requirementIdentifier === project.requirementIdentifier,
                            );
                            const score = progress?.score ?? null;
                            const complete = progress ? isPassedOrWaived(progress) : false;

                            return {
                                title: project.title,
                                requirementIdentifier: project.requirementIdentifier,
                                projectType: project.projectType,
                                'Rubric Score': score ? String(score) : null,
                                status: getProjectStatus(progress, project.projectType, cohort!),
                                started: !!progress,
                                complete,
                            };
                        }) || [],
                } as PlaylistsWithGrades;

                const complete =
                    (!!objectWithGrades.smartcases.length || !!objectWithGrades.projects.length) &&
                    objectWithGrades.smartcases.every(s => !!s.complete) &&
                    objectWithGrades.exams.every(e => !!e.completedAt) &&
                    objectWithGrades.projects.every(pr => pr.complete);

                const started =
                    objectWithGrades.smartcases?.some(s => !!s.started) ||
                    objectWithGrades.exams?.some(e => !!e.started) ||
                    objectWithGrades.projects?.some(pr => !!pr.started);

                objectWithGrades.complete = complete;
                objectWithGrades.started = started;

                if (cohort?.specializationPlaylistPackIds?.includes(p.localePackId)) {
                    specializationGrades.push(objectWithGrades);
                } else {
                    playlistGrades.push(objectWithGrades);
                }

                return [playlistGrades, specializationGrades];
            },
            [[] as PlaylistsWithGrades[], [] as PlaylistsWithGrades[]],
        );
    }, [
        cohort,
        user,
        playlistsAndSpecializations,
        foundationsPlaylist?.localePackId,
        projectsByPlaylist,
        assessmentCourses,
        lessonProgress,
        includeFoundationsInGrades,
        courseProgress,
        projectProgress,
        gradeSafeStreamLocalePackIds,
    ]);

    const capstoneWithGrades = useMemo(() => {
        const values = capstones.map(c => {
            const progress = projectProgress.find(pp => pp.requirementIdentifier === c.requirementIdentifier);
            const score = progress?.score ?? null;

            return {
                Title: c.title,
                'Rubric Score': score ? String(score) : null,
                status: getProjectStatus(progress, c.projectType, cohort!),
                started: !!progress,
            };
        });
        const complete = values.every(v => v.status !== '\u2014');
        const started = values.some(v => v.started);
        return { complete, started, values };
    }, [capstones, cohort, projectProgress]);

    const otherRequirementsWithGrades = useMemo(() => {
        const values = nonPlaylistRequiredCourses
            .filter(c => !c.exam)
            .flatMap(c =>
                c.lessons
                    .filter(l => !!l.assessment)
                    .map(l => {
                        const progress = lessonProgress.find(lp => lp.localePackId === l.localePack.id);

                        return {
                            title: l.title,
                            course: c.title,
                            score: formatScore(progress),
                            completedAt: progress?.completedAt
                                ? formattedUserFacingMonthDayYearMedium(new Date(progress.completedAt * 1000), false)
                                : null,
                            complete: progress?.complete ?? false,
                            started: !!progress,
                        };
                    }),
            );
        const started = values.some(v => !!v.started);
        const complete = values.every(v => !!v.complete);

        return { started, complete, values };
    }, [lessonProgress, nonPlaylistRequiredCourses]);

    const electivesWithGrades = useMemo(
        () =>
            electives.flatMap(e =>
                e.smartcases
                    .map(sc => {
                        const progress = lessonProgress.find(lp => lp.localePackId === sc.localePack.id);
                        if (!progress) return null;

                        // Apply same asterisk logic for electives as for main playlists
                        const electiveCourse = assessmentCourses.find(c =>
                            c.lessons.some(l => l.localePack.id === sc.localePack.id),
                        );

                        const addAsteriskForElective =
                            electiveCourse &&
                            shouldAddAsterisk(
                                electiveCourse.localePack.id,
                                includeFoundationsInGrades,
                                cohort?.foundationsLessonStreamLocalePackIds,
                                gradeSafeStreamLocalePackIds,
                            );

                        return {
                            course: addAsteriskForElective ? (
                                <div>
                                    {e.course}
                                    <p className="ms-[1px] inline-block pt-[1px] align-middle text-lg font-semibold leading-[0] text-beige-beyond-dark">
                                        *
                                    </p>
                                </div>
                            ) : (
                                e.course
                            ),
                            title: sc.title,
                            score: formatScore(progress),
                            completedAt: progress?.completedAt
                                ? formattedUserFacingMonthDayYearMedium(new Date(progress.completedAt * 1000), false)
                                : null,
                            complete: progress?.complete ?? false,
                            started: !!progress,
                            localePackId: sc.localePack.id,
                        };
                    })
                    .filter(v => !!v),
            ),
        [
            electives,
            lessonProgress,
            includeFoundationsInGrades,
            cohort?.foundationsLessonStreamLocalePackIds,
            gradeSafeStreamLocalePackIds,
            assessmentCourses,
        ],
    );

    const refundEligibilityInfo = useMemo(() => {
        const refundEntitlement = getRefundEntitlement(user);
        if (!isAssessmentPolicyWithCurriculumStarted(refundEntitlement)) return null;

        const {
            numExamsForPolicy,
            numCompletedExamsForPolicy,
            numSmartcasesForPolicy,
            numCompletedSmartcasesForPolicy,
            numRequiredProjectsForPolicy,
            numCompletedProjectsForPolicy,
            refundLevelAssessmentRanges,
        } = refundEntitlement.metadata;

        const hasCapstone = capstoneWithGrades.values.length > 0;
        const completedCapstone = hasCapstone && capstoneWithGrades.values.every(v => v.status !== '\u2014');
        const numCompletedProjects = hasCapstone
            ? Math.max(numCompletedProjectsForPolicy - (completedCapstone ? 1 : 0), 0)
            : numCompletedProjectsForPolicy;
        return {
            smartcases: { completed: numCompletedSmartcasesForPolicy, required: numSmartcasesForPolicy },
            exams: { completed: numCompletedExamsForPolicy, required: numExamsForPolicy },
            projects: {
                completed: numCompletedProjects,
                required: hasCapstone ? Math.max(numRequiredProjectsForPolicy - 1, 0) : numRequiredProjectsForPolicy,
            },
            capstone: { completed: completedCapstone ? 1 : 0, required: hasCapstone ? 1 : 0 },
            refundLevelAssessmentRanges,
        };
    }, [capstoneWithGrades.values, user]);

    return {
        playlistsWithGrades,
        specializationsWithGrades,
        otherRequirementsWithGrades,
        capstoneWithGrades,
        electivesWithGrades,
        refundEligibilityInfo,
        foundationsPlaylist,
        includeFoundationsInGrades,
        gradeSafeStreamLocalePackIds,
    };
};
